import { QueryClient, type QueryKey, type UseSuspenseQueryResult, useSuspenseQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { hours, minutes, seconds } from "@/3514/utils/time.utils"

import { EAssistantParticipantsQueryKey as EQueryKey } from "./assistant.participants.api.config"
import adapters from "./assistant.participants.api.queiries.adapters"
import fetchers from "./assistant.participants.api.queries.fetchers"
import type { IAssistanceParticipantDto, IAssistanceParticipantModel } from "./assistant.participants.api.types"
import mockedDtos from "./assistant.participants.mocks"

type TAssistantParticipantsSuspenseQuery = UseSuspenseQueryResult<IAssistanceParticipantModel[], AxiosError>

const IS_DEBUG: boolean = false

function useAssistantParticipantsSuspenseQuery(): ReturnType<() => TAssistantParticipantsSuspenseQuery> {
    return useSuspenseQuery<IAssistanceParticipantDto[], AxiosError, IAssistanceParticipantModel[], QueryKey>({
        queryKey: EQueryKey.Participants(),
        queryFn: fetchers.participants,
        select: adapters.participants,
        staleTime: hours(1),
        gcTime: minutes(30),
        retry: 1,
        retryDelay: seconds(5),
        refetchOnReconnect: true,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        networkMode: "online",
        notifyOnChangeProps: ["data"],
        ...(IS_DEBUG ? { initialData: mockedDtos } : {})
    })
}

async function assistantParticipantsQueryPrefetcher({ qc }: { qc: QueryClient }): Promise<void> {
    await qc.prefetchQuery({
        queryKey: EQueryKey.Participants(),
        queryFn: fetchers.participants,
        staleTime: hours(1),
        gcTime: minutes(30),
        retry: false
    })
}

async function assistantParticipantsQueryInvalidator({ qc }: { qc: QueryClient }): Promise<void> {
    await qc.invalidateQueries({ queryKey: EQueryKey.Participants() })
}

export {
    useAssistantParticipantsSuspenseQuery,
    assistantParticipantsQueryPrefetcher,
    assistantParticipantsQueryInvalidator
}
export type { TAssistantParticipantsSuspenseQuery }
